import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { Box, IconButton, Modal, useMediaQuery, useTheme } from '@mui/material';

import { CustomDropZone } from '~/components/atoms/CustomDropZone';
import { Button } from '~/components/ui/button';
import Slider from '~/components/ui/Slider';
import { Text } from '~/components/ui/text';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { COLORS } from '~/styles/colors';
import { UploadedFile } from '~/types/qrCode';
import { getRotateValue } from '~/utils/common';

import { ModalContainer, StyledUploadedImage, StyledUploadedImageContainer } from './styled';

const DEFAULT_ZOOM = 100;
const DEFAULT_ROUNDNESS = 0;
const DEFAULT_ROTATE = 0;

type ImageUploaderModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSave?: (data: { file: UploadedFile | null; rotate: number; zoom: number; roundness: number }) => void;
  image?: string;
  defaultZoom?: number;
  defaultRotate?: number;
  defaultRoundness?: number;
};

export default function ImageUploaderModal({
  open,
  setOpen,
  onSave,
  image,
  defaultZoom,
  defaultRotate,
  defaultRoundness,
}: ImageUploaderModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [uploadedImage, setUploadedImage] = useState<Partial<UploadedFile> | null>(
    image ? { previewLink: image } : null,
  );
  const [rotate, setRotate] = useState<number>(typeof defaultRotate === 'number' ? defaultRotate : DEFAULT_ROTATE);
  const [zoom, setZoom] = useState<number>(typeof defaultZoom === 'number' ? defaultZoom : DEFAULT_ZOOM);
  const [roundness, setRoundness] = useState<number>(
    typeof defaultRoundness === 'number' ? defaultRoundness : DEFAULT_ROUNDNESS,
  );

  const handleClose = () => setOpen(false);

  const addCoverImage = (files: UploadedFile[]) => {
    setUploadedImage(files[0]);
  };

  const handleResetImages = () => {
    setUploadedImage(null);
    setZoom(DEFAULT_ZOOM);
    setRoundness(DEFAULT_ROUNDNESS);
    setRotate(DEFAULT_ROTATE);
  };

  const handleSaveImage = () => {
    if (onSave) {
      onSave({ file: uploadedImage, rotate, zoom, roundness });
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      slotProps={{ backdrop: { sx: { backgroundColor: COLORS.grey300Opacity30, backdropFilter: 'blur(3px)' } } }}
    >
      <ModalContainer>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: '24px' }}>
          <Text variant="h2">{t('uploaderModal:title')}</Text>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {uploadedImage?.previewLink ? (
          <StyledUploadedImageContainer>
            <StyledUploadedImage
              src={uploadedImage?.previewLink}
              $rotate={rotate}
              $zoom={zoom}
              $roundness={roundness}
              alt="uploaded image"
            />
          </StyledUploadedImageContainer>
        ) : (
          <CustomDropZone
            maxSizeInKb={15000}
            maxSize={15}
            fileTypes="PNG/JPEG"
            onDrop={(files) => addCoverImage(files)}
            acceptTypes="image/*"
          />
        )}

        <Box
          display="flex"
          sx={{
            flex: 1,
            marginTop: '24px',
            gap: '16px',
          }}
          flexDirection={isDesktop ? 'row' : 'column'}
        >
          <Box display="flex" flex={1} alignItems="center" sx={{ gap: '16px' }}>
            <Text variant="input15" sx={{ color: !uploadedImage ? COLORS.grey200 : undefined }}>
              {t('uploaderModal:zoom')}
            </Text>
            <Slider
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              valueLabelDisplay="auto"
              disabled={!uploadedImage}
              sx={{
                width: isDesktop ? '110px' : 'unset',
                flex: isDesktop ? 'none' : 1,
              }}
            />
          </Box>
          <Box display="flex" flex={1} alignItems="center" sx={{ gap: '16px' }}>
            <Text variant="input15" sx={{ color: !uploadedImage ? COLORS.grey200 : undefined }}>
              {t('uploaderModal:roundness')}
            </Text>
            <Slider
              value={roundness}
              onChange={(e) => setRoundness(e.target.value)}
              valueLabelDisplay="auto"
              disabled={!uploadedImage}
              sx={{
                width: isDesktop ? '110px' : 'unset',
                flex: isDesktop ? 'none' : 1,
              }}
            />
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection={isDesktop ? 'row' : 'column'}
          justifyContent="center"
          mt={4}
          sx={{ marginInline: '50px', gap: '16px' }}
        >
          <Button
            type="primary-outlined"
            disabled={!uploadedImage}
            onClick={() => setRotate(getRotateValue(rotate))}
            startIcon={<RotateLeftIcon />}
            size="xs"
            sx={{ flex: 1 }}
          >
            {t('uploaderModal:rotateBtn')}
          </Button>
          <Button
            onClick={handleResetImages}
            disabled={!uploadedImage}
            type="primary-outlined"
            size="xs"
            sx={{ flex: 1 }}
          >
            {t('uploaderModal:resetBtn')}
          </Button>
        </Box>

        <Button
          disabled={!uploadedImage}
          onClick={handleSaveImage}
          type="contained"
          sx={{ margin: '24px auto 0', height: '40px', width: '135px' }}
        >
          {t('uploaderModal:saveBtn')}
        </Button>
      </ModalContainer>
    </Modal>
  );
}
